import { useState, type ReactElement } from 'react';
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Package managers whose install commands Agents Activity recognizes (KIR-3483).
 * Grouped by ecosystem purely for readability. Mirrors the command classifier
 * in the backend `parse-command` handler — keep in sync when a manager is added.
 */
const TRACKED_MANAGERS: ReadonlyArray<{ ecosystem: string; managers: string }> =
  [
    { ecosystem: 'JavaScript', managers: 'npm, yarn, pnpm' },
    { ecosystem: 'Python', managers: 'pip, uv, poetry, pipenv' },
    { ecosystem: 'Ruby', managers: 'gem, bundler' },
    { ecosystem: 'Go', managers: 'go modules' },
    { ecosystem: 'Rust', managers: 'cargo' },
    { ecosystem: 'PHP', managers: 'composer' },
    { ecosystem: 'Java', managers: 'maven' },
    { ecosystem: '.NET', managers: 'dotnet (NuGet)' },
  ];

/**
 * "What is tracked?" help button + dialog for Agents Activity (KIR-3483).
 * UI-only: a short explanation of what the screen records plus the list of
 * supported package managers.
 */
export function WhatIsTrackedDialog(): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
        >
          <HelpCircle className="size-4" />
          What is tracked?
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What is tracked?</DialogTitle>
          <DialogDescription>
            Kirin records the packages your AI coding agents install and the
            repositories they clone or pull via terminal commands, so you can
            see what code was pulled into your environment.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            Supported package managers
          </span>
          <ul className="flex flex-col gap-1.5 text-sm">
            {TRACKED_MANAGERS.map(({ ecosystem, managers }) => (
              <li key={ecosystem} className="grid grid-cols-[96px_1fr] gap-2">
                <span className="text-muted-foreground">{ecosystem}</span>
                <span>{managers}</span>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

WhatIsTrackedDialog.displayName = 'WhatIsTrackedDialog';
